import type {
  FilterBarItem,
  FilterItem,
} from '@wconcept/wconcept-common/models/renewal/common';
import { ModalsContext } from '@wconcept/wconcept-ui-renewal/src/contexts/ModalsContext';
import { useSearchParams } from 'next/navigation';
import { useCallback, useContext } from 'react';
import useGlobalState from '@hooks/useGlobalState';
import LpFilterModalV2 from '../LpFilterModalV2';
import {
  getLpProductV2QueryString,
  getLpProductV2RetryFilterTab,
} from '../filterV2';
import type { LpFilterProductListProps } from '../types';

type LpFilterModalOptions = Pick<
  LpFilterProductListProps,
  | 'filterBarList'
  | 'lastFilterTabRef'
  | 'selectedFilters'
  | 'originalFilters'
  | 'onFilterSubmit'
  | 'onFilterClose'
  | 'requestProductAggByType'
  | 'requestProductCount'
  | 'hideFilterOptionCounts'
  | 'hiddenSelectedFilterTypes'
>;

interface LpFilterModalGlobalState {
  readonly isMobile?: boolean;
  readonly appInfo: {
    readonly isIPhoneXApp: boolean;
    readonly isNativeAppBoth?: boolean;
  };
}

export default function useLpFilterModalV2(options: LpFilterModalOptions): {
  openFilterModal: (item?: FilterBarItem) => void;
  submit: (nextSelectedFilters: FilterItem[], type?: string) => void;
} {
  const {
    filterBarList,
    lastFilterTabRef,
    selectedFilters = [],
    originalFilters,
    onFilterSubmit,
    onFilterClose,
    requestProductAggByType,
    requestProductCount,
    hideFilterOptionCounts,
    hiddenSelectedFilterTypes,
  } = options;
  const searchParams = useSearchParams();
  const { openModal } = useContext(ModalsContext);
  const { isMobile, appInfo } = useGlobalState<LpFilterModalGlobalState>();
  const shouldManageNativeActionBar =
    appInfo.isNativeAppBoth && searchParams.get('gnbType') === 'Y';

  // 선택칩 삭제처럼 해제만 하는 경우는 'remove'로 넘긴다.
  // 페이지 훅이 'submit'에만 filter_click을 붙이므로 쿼리는 그대로 반영되고 로그는 빠진다
  const submit = useCallback(
    (nextSelectedFilters: FilterItem[], type = 'submit'): void => {
      onFilterSubmit?.(
        getLpProductV2QueryString(searchParams, nextSelectedFilters),
        nextSelectedFilters,
        type
      );
    },
    [onFilterSubmit, searchParams]
  );

  const openFilterModal = useCallback(
    (
      item = getLpProductV2RetryFilterTab(
        filterBarList,
        selectedFilters,
        lastFilterTabRef.current
      )
    ): void => {
      if (!originalFilters || !item) return;
      lastFilterTabRef.current = item.value;

      if (shouldManageNativeActionBar) {
        document.location.href = 'wconceptkr://actionBarOff';
      }

      openModal(LpFilterModalV2, {
        placement: isMobile ? 'bottom' : 'left',
        value: {
          selectedFilterList: selectedFilters,
          selectedTab: item.value,
          onTabChange: (tab: string) => {
            lastFilterTabRef.current = tab;
          },
          isDesktop: !isMobile,
          isIPhoneXApp: appInfo.isIPhoneXApp,
          tabListData: filterBarList,
          originalFilters,
          requestFilter: requestProductAggByType,
          requestCount: requestProductCount,
          hideFilterOptionCounts,
          hiddenSelectedFilterTypes,
        },
        onSubmit: (nextSelectedFilters: FilterItem[]) => {
          if (shouldManageNativeActionBar) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
          submit(nextSelectedFilters);
        },
        onClose: () => {
          if (shouldManageNativeActionBar) {
            document.location.href = 'wconceptkr://actionBarOn';
          }
          onFilterClose?.();
        },
      });
    },
    [
      appInfo.isIPhoneXApp,
      filterBarList,
      lastFilterTabRef,
      hideFilterOptionCounts,
      hiddenSelectedFilterTypes,
      isMobile,
      openModal,
      onFilterClose,
      originalFilters,
      requestProductAggByType,
      requestProductCount,
      selectedFilters,
      shouldManageNativeActionBar,
      submit,
    ]
  );

  return { openFilterModal, submit };
}
